import React from 'react';
import SmartProductImage from './SmartProductImage';
import { shopifyResize } from './previewImage';
import { handleMissingImage, handleWhiteBgDetection } from './pressingHelpers';
import { useT } from './i18n/I18nContext';

export default function QCBundleCard({
    unit, pickStatus, failReason, onClick, scanOnly, overrideActive,
    dragAttrs, dragListeners, dragRef, isDragging,
    skuNotification,
    tpOverridesByName,   // { playerName: {override_number, order_number, ...} }
}) {
    const t = useT();
    // TP override: RDS number wins over the checkout-embedded playerNumber.
    // Yellow-highlighted badge tells QC the number was updated by the club
    // in the Team Portal after the order was placed.
    const rawPlayerNumber = unit.playerNumber;
    const tpOverride = unit.playerName && tpOverridesByName ? tpOverridesByName[unit.playerName] : null;
    const isTpOverride = !!(tpOverride
        && String(tpOverride.override_number || '') !== String(rawPlayerNumber || ''));
    const displayPlayerNumber = isTpOverride ? tpOverride.override_number : rawPlayerNumber;
    const slots = [];
    if (unit.previewFront) slots.push({ key: 'front', label: t('Front'),     src: shopifyResize(unit.previewFront, 800) });
    if (unit.previewBack)  slots.push({ key: 'back',  label: t('Back side'), src: shopifyResize(unit.previewBack,  800) });
    if (slots.length === 0) slots.push({ key: 'single', label: '', src: null, fallback: true });
    const imgsClass = `qcb-card__imgs${slots.length === 1 ? ' qcb-card__imgs--single' : ''}`;

    return (
        <div
            ref={dragRef}
            className={`qcb-card${isDragging ? ' qcb-card--dragging' : ''}${scanOnly ? ' qcb-card--scan-only' : ''}${overrideActive ? ' ppb-card--override-active' : ''}`}
            onClick={onClick}
            {...(dragAttrs || {})}
            {...(dragListeners || {})}
        >
            <div className="qcb-card__head">
                <div className="qcb-card__name">
                    {unit.playerName || unit.title}
                </div>
                {displayPlayerNumber && (
                    <div className="qcb-card__number"
                         style={isTpOverride ? {
                             background: '#fef3c7',
                             color: '#78350f',
                             padding: '2px 8px',
                             borderRadius: 6,
                             border: '1px solid #fbbf24',
                         } : undefined}
                         title={isTpOverride
                             ? `Team Portal override — order had #${rawPlayerNumber}, updated to #${displayPlayerNumber}`
                             : undefined}>
                        #{displayPlayerNumber}
                    </div>
                )}
            </div>
            <div className="qcb-card__garment">
                <span className="qcb-card__garment-name">{unit.title}</span>
                {unit.variantTitle && <span className="qcb-card__variant">{unit.variantTitle}</span>}
                {unit.sku && (
                    <span className="qcb-card__sku">
                        {unit.sku}
                    </span>
                )}
                {skuNotification && (
                    <div style={{
                        display: 'inline-block',
                        marginLeft: 8,
                        padding: '5px 12px',
                        borderRadius: 14,
                        background: '#fde8e8',
                        color: '#c00',
                        fontWeight: 700,
                        fontSize: '1em',
                        border: '2px solid #fbb',
                        verticalAlign: 'middle',
                    }}>
                        {skuNotification}
                    </div>
                )}
            </div>
            {unit.totalInstances > 1 && (
                <div className="qcb-card__instance">({unit.instanceIndex}/{unit.totalInstances})</div>
            )}
            <div className={imgsClass}>
                {slots.map(s => (
                    <div key={s.key} className="qcb-card__img">
                        {s.fallback
                            ? unit.imageUrl
                                ? <SmartProductImage src={unit.imageUrl} alt="" transparent />
                                : <span className="qcb-card__img-fallback">📦</span>
                            : <img
                                src={s.src}
                                alt={s.label}
                                draggable={false}
                                onLoad={handleWhiteBgDetection}
                                onError={handleMissingImage}
                              />}
                        {s.label && <span className="qcb-card__img-label">{s.label}</span>}
                    </div>
                ))}
            </div>
            {/* All warning chips share a single flex row so Not Picked +
                Not Pressed sit side-by-side instead of stacking (previously
                each chip was a direct flex-column child of .qcb-card, so
                each got its own row). */}
            <div className="qcb-card__warns">
                {(Array.isArray(pickStatus) ? pickStatus : [pickStatus]).includes('not-picked') && (
                    <span className="qcb-card__warn qcb-card__warn--amber">{t('Not Picked')}</span>
                )}
                {(Array.isArray(pickStatus) ? pickStatus : [pickStatus]).includes('not-pressed') && (
                    <span className="qcb-card__warn qcb-card__warn--red">{t('Not Pressed')}</span>
                )}
                {failReason && (
                    <span className="qcb-card__warn qcb-card__warn--red">{t(failReason)}</span>
                )}
                {unit.bundleRecovery && (
                    <span className="qcb-card__warn qcb-card__warn--amber" title={t('Embellishment was marked shipped in Shopify in an earlier wave, but the kit is incomplete — please verify and press.')}>
                        {t('Bundle recovery — verify embellishment')}
                    </span>
                )}
            </div>
        </div>
    );
}
